import { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Typography,
  Link,
} from "@mui/material";

import * as Yup from "yup";
import { Formik } from "formik";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAppContext } from "../../context/AppContext";
import { useNavigate } from "react-router-dom";

const LoginForm = ({ ...others }) => {
  const { login } = useAppContext();
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#fff", // White background for clarity
      transition: "all 0.2s ease-in-out",
      "& fieldset": {
        borderColor: "rgba(0, 0, 0, 0.1)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(47, 95, 219, 0.5)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#2f5fdb",
        borderWidth: "1.5px",
      },
    },
    "& .MuiInputBase-input": {
      fontSize: "0.9rem",
      color: "#1e293b", // Dark slate / Black text
      padding: "12px 14px",
      "&::placeholder": {
        color: "#94a3b8",
        opacity: 1
      }
    },
  };

  return (
    <>
      <Formik
        initialValues={{
          email: "",
          password: "",
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string()
            .email("Invalid email address")
            .max(255)
            .required("Email is required"),
          password: Yup.string().max(255).required("Password is required"),
        })}
        onSubmit={async (values, { setErrors, setSubmitting }) => {
          try {
            const url = await login({ loginUser: values, remember: true });
            if (url !== "") navigate(url);
          } catch (err) {
            setErrors({ submit: err?.response?.data?.message || "Login failed" });
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
        }) => (
          <form noValidate onSubmit={handleSubmit} {...others}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="body2" sx={{ color: "#9ca3af", fontWeight: 500, mb: 1, ml: 0.5 }}>
                  Email
                </Typography>
                <FormControl fullWidth error={Boolean(touched.email && errors.email)} sx={inputStyles}>
                  <OutlinedInput
                    id="email-login"
                    type="email"
                    value={values.email}
                    name="email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="sample@email.com"
                  />
                  {touched.email && errors.email && (
                    <FormHelperText error id="helper-text-email-login" sx={{ color: "#ef4444" }}>
                      {errors.email}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ color: "#9ca3af", fontWeight: 500, mb: 1, ml: 0.5 }}>
                  Password
                </Typography>
                <FormControl fullWidth error={Boolean(touched.password && errors.password)} sx={inputStyles}>
                  <OutlinedInput
                    id="password-login"
                    type={showPassword ? "text" : "password"}
                    value={values.password}
                    name="password"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="Enter your password here"
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="small"
                          sx={{ mr: 0.5, color: "rgba(0,0,0,0.4)" }}
                        >
                          {showPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {touched.password && errors.password && (
                    <FormHelperText error id="helper-text-password-login" sx={{ color: "#ef4444" }}>
                      {errors.password}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>

              <Box sx={{ position: "relative" }}>
                <Button
                  disableElevation
                  disabled={isSubmitting}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  sx={{
                    py: 1.5,
                    borderRadius: "8px",
                    fontSize: "1rem",
                    fontWeight: 500,
                    textTransform: "none",
                    background: "linear-gradient(90deg, #2f5fdb 0%, #e8a53d 55%, #d93025 100%)",
                    color: "#fff",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      background: "linear-gradient(90deg, #24499f 0%, #c98a2c 55%, #b02419 100%)",
                      boxShadow: "0 8px 25px rgba(47, 95, 219, 0.35)",
                    },
                  }}
                >
                  {isSubmitting ? "Logging in..." : "Login"}
                </Button>
              </Box>

              <Box sx={{ textAlign: "center", mt: -1 }}>
                <Link
                  href="/reset-password"
                  underline="none"
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: "#2f5fdb",
                    transition: "color 0.2s",
                    "&:hover": { color: "#e8a53d" },
                  }}
                >
                  Forgot Password?
                </Link>
              </Box>

              {errors.submit && (
                <Box sx={{ mt: 1 }}>
                  <FormHelperText error sx={{ fontSize: "14px", fontWeight: 500, textAlign: "center", color: "#ef4444" }}>
                    {errors.submit}
                  </FormHelperText>
                </Box>
              )}
            </Stack>
          </form>
        )}
      </Formik>
    </>
  );
};

export default LoginForm;
